import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { DestructiveButton } from 'ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { useToastNotification } from 'utils/hooks/useToastNotification';

import { useUserDisplayName } from '../../hooks';
import { useListModalContext } from '../context/ListModalContext';
import { ListRemoveOptions } from '../types';
import { useListsContext } from '../../contexts/ListsContext';

const displayContent = {
  remove: 'Remove from list',
};

const BoldText = styled.b`
  font-weight: bold;
`;

export const RemoveUserFromListModal = () => {
  const { closeModal, listModalOptions } = useListModalContext();
  const userDisplayName = useUserDisplayName();
  const { unsubscribe, isUnsubscribing, didUnsubscribe, couldNotUnsubscribe, resetUnsubscribe } =
    useListsContext();

  const removeFromList = useMemo(
    () => (listModalOptions as ListRemoveOptions | undefined)?.fromList,
    [listModalOptions],
  );

  const handleClose = useCallback(() => {
    closeModal();
    resetUnsubscribe();
  }, [closeModal, resetUnsubscribe]);

  useToastNotification({
    enabled: removeFromList && (didUnsubscribe || couldNotUnsubscribe),
    negative: couldNotUnsubscribe,
    positive: didUnsubscribe,
    onDone: handleClose,
    negativeMessage: (
      <>
        Could not remove <BoldText>{userDisplayName}</BoldText> from{' '}
        <BoldText>{removeFromList?.name}</BoldText>
      </>
    ),
    positiveMessage: (
      <>
        Removed <BoldText>{userDisplayName}</BoldText> from{' '}
        <BoldText>{removeFromList?.name}</BoldText>
      </>
    ),
  });

  if (!removeFromList) return null;

  const handleClick = () => {
    if (removeFromList) unsubscribe(removeFromList);
  };

  return (
    <Modal
      id="list-remove"
      withPortal
      isModalOpen
      onClose={handleClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader>{displayContent.remove}</ModalHeader>
      <ModalBody>
        <p>
          Are you sure you want to remove <BoldText>{userDisplayName}</BoldText> from{' '}
          <BoldText>{removeFromList.name}</BoldText>?
        </p>
      </ModalBody>
      <ModalFooter>
        <DestructiveButton onClick={handleClick} isDisabled={isUnsubscribing}>
          {displayContent.remove}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
